import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Messages } from 'primereact/messages'
import { Tag } from 'primereact/tag'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import {
  SupplierTemplate,
  useAllSupplierTemplates,
} from '../../../supplier/data'
import { AddSupplierFormDto } from '../../data/model'
import { FormFieldName } from '../AddSupplier.form'

type Props = {
  formik: FormikProps<AddSupplierFormDto>
  isFormFieldInvalid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => JSX.Element
  isRequestRunning: boolean
}
export const SupplierInformation: React.FC<Props> = ({
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
  isRequestRunning,
}) => {
  const { t } = useTranslation()
  const { data: supplierTemplates } = useAllSupplierTemplates()
  // const { uiConfig: config } = useConfigContext()
  const messagesRef = useRef<Messages>(null)

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.clear()
      messagesRef.current.show({
        id: '1',
        sticky: true,
        severity: 'info',
        detail: t('supplier.supplierInformation.message.detail'),
        closable: false,
      })
    }
  }, [])

  const templateDropdownItemTemplate = (option: SupplierTemplate) => {
    return (
      <>
        <div className="flex align-items-center">
          <div className="flex flex-column my-1 mr-3">
            <div className="flex justify-content-between">
              <div className="font-semibold">{option.name}</div>
              <div>
                {option.isdefault ? <Tag value="default" rounded /> : null}
              </div>
            </div>
            <div className="text-sm font-italic max-w-full">
              {option.description}
            </div>
          </div>
          {option.id === formik.values.template && (
            <div className="ml-auto">
              <i className="pi pi-check"></i>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <PanelContainer
      header={t('supplier.supplierInformation.title')}
      id="addSupplier-supplierInformation"
      toggleable={false}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <p>{t('supplier.supplierInformation.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Messages ref={messagesRef} />

          <div className="formgrid grid">
            {/* template */}
            <div className="col-12">
              <label
                className={classNames('text-sm block mb-2', {
                  'p-error': isFormFieldInvalid('template'),
                })}
                htmlFor="template"
              >
                <span className="text-red-500 font-bold mr-1">*</span>
                {t('supplier.supplierInformation.form.template.label')}
              </label>
              <Dropdown
                id="template"
                options={supplierTemplates?.sort(
                  (a: SupplierTemplate, b: SupplierTemplate) =>
                    a.name < b.name ? 1 : -1,
                )}
                optionLabel="name"
                optionValue="id"
                placeholder={t(
                  'supplier.supplierInformation.form.template.placeholder',
                )}
                value={formik.values.template}
                onChange={formik.handleChange}
                itemTemplate={templateDropdownItemTemplate}
                filter
                filterBy="name,id"
                disabled={isRequestRunning}
                className={classNames('w-full', {
                  'p-invalid': isFormFieldInvalid('template'),
                })}
              />
              {getFormErrorMessage('template')}
            </div>
          </div>
          <div className="formgrid grid">
            {/* Id */}
            <div className="col-12 md:col-4">
              <label
                className={classNames('text-sm block mb-2', {
                  'p-error': isFormFieldInvalid('id'),
                })}
                htmlFor="id"
              >
                <span className="text-red-500 font-bold mr-1">*</span>
                {t('supplier.supplierInformation.form.id.label')}
              </label>
              <InputText
                id="id"
                value={formik.values.id}
                onChange={formik.handleChange}
                className={classNames('w-full', {
                  'p-invalid': isFormFieldInvalid('id'),
                })}
                placeholder={t(
                  'supplier.supplierInformation.form.id.placeholder',
                )}
                disabled={isRequestRunning}
              />
              <div>{getFormErrorMessage('id')}</div>
            </div>

            {/* Name */}
            <div className="col-12 md:col-8">
              <label
                className={classNames('text-sm block mb-2', {
                  'p-error': isFormFieldInvalid('name'),
                })}
                htmlFor="name"
              >
                <span className="text-red-500 font-bold mr-1">*</span>
                {t('supplier.supplierInformation.form.name.label')}
              </label>
              <InputText
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className={classNames('w-full', {
                  'p-invalid': isFormFieldInvalid('name'),
                })}
                disabled={isRequestRunning}
              />
              <div>{getFormErrorMessage('name')}</div>
            </div>

            {/* Description */}
            <div className="col-12">
              <label
                className={classNames('text-sm block mb-2', {
                  'p-error': isFormFieldInvalid('description'),
                })}
                htmlFor="description"
              >
                {t('supplier.supplierInformation.form.description.label')}
              </label>
              <InputTextarea
                id="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                rows={5}
                className={classNames('w-full', {
                  'p-invalid': isFormFieldInvalid('description'),
                })}
                disabled={isRequestRunning}
              />
            </div>
          </div>
        </div>
      </div>
    </PanelContainer>
  )
}
